
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import axios from "axios";
import { mdiClose } from "@mdi/js";

interface SyncMisPreviewRow {
  id: number;
  misCode: string;
  caption: string;
  currentPrice: number | null;
  newPrice: number | null;
  status: "changed" | "unchanged" | "not_found";
}

@Component
export default class SyncMisDialog extends Vue {
  @Prop() value!: boolean;
  @Prop({ required: true }) catalogId!: number | string;
  $message: any;

  icons: any = { close: mdiClose };

  loading = false;
  applying = false;
  error: string | null = null;
  filialExternalId = "";
  rows: SyncMisPreviewRow[] = [];
  filterStatus: "all" | "changed" | "unchanged" | "not_found" = "all";

  get totalCount() {
    return this.rows.length;
  }
  get changedCount() {
    return this.rows.filter((r) => r.status === "changed").length;
  }
  get unchangedCount() {
    return this.rows.filter((r) => r.status === "unchanged").length;
  }
  get notFoundCount() {
    return this.rows.filter((r) => r.status === "not_found").length;
  }
  get filteredRows() {
    return this.filterStatus === "all"
      ? this.rows
      : this.rows.filter((r) => r.status === this.filterStatus);
  }

  @Watch("value")
  async onValueChange(v: boolean) {
    if (v) await this.loadPreview();
  }

  async loadPreview() {
    this.loading = true;
    this.error = null;
    this.rows = [];
    this.filialExternalId = "";
    this.filterStatus = "all";
    try {
      const { data } = await axios.post(
        `manage/pricecatalog/${this.catalogId}/sync-mis/preview`
      );
      this.filialExternalId = data.filialExternalId ?? "";
      this.rows = data.rows ?? [];
    } catch (e) {
      this.error = this.extractError(e);
    } finally {
      this.loading = false;
    }
  }

  async apply() {
    const changed = this.rows.filter((r) => r.status === "changed");
    if (!changed.length) return;
    this.applying = true;
    try {
      await axios.post(`manage/pricecatalog/${this.catalogId}/sync-mis/apply`, {
        items: changed.map((r) => ({ id: r.id, newPrice: r.newPrice })),
      });
      this.$message?.("Цены обновлены");
      this.$emit("applied");
      this.$emit("input", false);
    } catch (e) {
      this.error = this.extractError(e);
    } finally {
      this.applying = false;
    }
  }

  close() {
    if (this.loading || this.applying) return;
    this.$emit("input", false);
  }

  formatPrice(p: number | null): string {
    if (p === null || p === undefined) return "—";
    return new Intl.NumberFormat("ru-RU", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(p);
  }

  statusLabel(s: SyncMisPreviewRow["status"]): string {
    return s === "changed"
      ? "Изменится"
      : s === "unchanged"
      ? "Без изменений"
      : "Нет в МИС";
  }

  statusColor(s: SyncMisPreviewRow["status"]): string {
    return s === "changed" ? "warning" : s === "unchanged" ? "grey" : "error";
  }

  extractError(e: any): string {
    return (
      e?.response?.data?.error ??
      e?.response?.data?.message ??
      e?.response?.data ??
      e?.message ??
      "Ошибка"
    );
  }
}
